import { useState, useEffect } from "react";
import { supabase } from "./supabase";

// ─── PIQ AUTH GATE ───────────────────────────────────────────────────────────
// Shared across AmortIQ and IntercoIQ. Handles session, login/signup,
// first-run onboarding (create organization + optional sample data), and
// hands { session, profile, org, role, signOut, refreshOrg } to the app.

const ym = (offsetMonths = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offsetMonths);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const firstOfMonth = (ymStr) => `${ymStr}-01`;

const AUTH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,700;9..144,900&display=swap');
.pq-auth-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#F0EEE6;font-family:'IBM Plex Sans',sans-serif;color:#403D35;padding:24px}
.pq-card{background:#FAF9F5;border:1px solid #E3DED0;border-radius:12px;box-shadow:0 8px 32px rgba(26,25,21,.08);padding:36px;width:100%;max-width:400px}
.pq-logo{font-family:'Fraunces',serif;font-size:26px;font-weight:900;color:#1A1915;letter-spacing:-.4px;text-align:center}
.pq-logo em{color:#C15F3C;font-style:normal}
.pq-sub{text-align:center;font-size:12px;color:#6F6A5C;margin:4px 0 24px;letter-spacing:.08em;text-transform:uppercase}
.pq-lbl{display:block;font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#6F6A5C;font-weight:600;margin:14px 0 4px}
.pq-inp{width:100%;background:#fff;border:1px solid #D2CBB8;border-radius:6px;padding:9px 11px;font-family:inherit;font-size:14px;color:#1A1915;outline:none;box-sizing:border-box}
.pq-inp:focus{border-color:#C15F3C}
.pq-btn{width:100%;margin-top:20px;background:#C15F3C;border:1px solid #C15F3C;border-radius:6px;color:#fff;font-family:inherit;font-size:13px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;padding:10px;cursor:pointer}
.pq-btn:hover{background:#A94E30}
.pq-btn:disabled{opacity:.5;cursor:not-allowed}
.pq-btn-ghost{background:none;border-color:#D2CBB8;color:#6F6A5C;margin-top:10px}
.pq-btn-ghost:hover{background:#E9E5D9;color:#403D35}
.pq-err{margin-top:14px;padding:9px 12px;border:1px solid rgba(179,56,44,.35);border-radius:6px;background:rgba(179,56,44,.06);color:#B3382C;font-size:12.5px;line-height:1.45}
.pq-toggle{margin-top:18px;text-align:center;font-size:12.5px;color:#6F6A5C}
.pq-toggle a{color:#C15F3C;cursor:pointer;font-weight:600;text-decoration:none}
.pq-note{margin-top:16px;text-align:center;font-size:11px;color:#B7B09E}
.pq-busy{text-align:center;padding:18px 0 6px;font-size:12.5px;color:#6F6A5C}
.pq-pwwrap{position:relative}
.pq-pwwrap .pq-inp{padding-right:42px}
.pq-eye{position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;color:#6F6A5C;cursor:pointer;padding:5px;display:flex;align-items:center;border-radius:5px}
.pq-eye:hover{color:#C15F3C;background:#F2EFE7}
.pq-mismatch{margin-top:6px;font-size:11.5px;color:#9A6A00}
.pq-rules{margin-top:8px;display:flex;flex-direction:column;gap:3px}
.pq-rule{display:flex;align-items:center;gap:7px;font-size:11.5px;color:#6F6A5C}
.pq-rule .pq-dot{width:15px;height:15px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;border:1px solid #D2CBB8;color:transparent;flex-shrink:0}
.pq-rule.ok{color:#2E7D4F}
.pq-rule.ok .pq-dot{background:#2E7D4F;border-color:#2E7D4F;color:#fff}
`;

// ─── SAMPLE DATA (one unified entity list feeding both modules) ──────────────

const SAMPLE_ENTITIES = [
  { name: "Corporate",          entity_type: "corporate", units: 0,   employees: 12, region: "Corporate", division: "Corporate",   ownership: "Owned"   },
  { name: "Elmwood Apartments", entity_type: "property",  units: 120, employees: 4,  region: "Southeast", division: "Affordable",  ownership: "Owned"   },
  { name: "Riverside Lofts",    entity_type: "property",  units: 48,  employees: 2,  region: "Northeast", division: "Market Rate", ownership: "Managed" },
  { name: "The Carrington",     entity_type: "property",  units: 210, employees: 7,  region: "Southeast", division: "Luxury",      ownership: "Owned"   },
  { name: "Millbrook Commons",  entity_type: "property",  units: 72,  employees: 3,  region: "Midwest",   division: "Affordable",  ownership: "Managed" },
  { name: "Sunstone Villas",    entity_type: "property",  units: 95,  employees: 3,  region: "Southeast", division: "Market Rate", ownership: "Owned"   },
  { name: "Harbor View Tower",  entity_type: "property",  units: 300, employees: 10, region: "Northeast", division: "Luxury",      ownership: "Owned"   },
  { name: "Pine Ridge Estates", entity_type: "property",  units: 60,  employees: 2,  region: "Midwest",   division: "Affordable",  ownership: "Managed" },
  { name: "Westgate Flats",     entity_type: "property",  units: 88,  employees: 3,  region: "Southwest", division: "Market Rate", ownership: "Managed" },
];

const SAMPLE_GLS = [
  { code: "1500-001", name: "Prepaid Insurance",        category: "prepaid_asset" },
  { code: "1500-002", name: "Prepaid Software",         category: "prepaid_asset" },
  { code: "1500-003", name: "Prepaid D&O/E&O",          category: "prepaid_asset" },
  { code: "1500-004", name: "Prepaid Maintenance",      category: "prepaid_asset" },
  { code: "1500-005", name: "Prepaid Licenses",         category: "prepaid_asset" },
  { code: "1500-099", name: "Prepaid — Other",          category: "prepaid_asset" },
  { code: "6200-001", name: "Insurance Expense",        category: "expense" },
  { code: "6200-002", name: "Software Expense",         category: "expense" },
  { code: "6200-003", name: "D&O/E&O Expense",          category: "expense" },
  { code: "6300-001", name: "Maintenance Expense",      category: "expense" },
  { code: "6400-001", name: "Licenses Expense",         category: "expense" },
  { code: "6500-001", name: "G&A Expense",              category: "expense" },
  { code: "6100", name: "Management Fees",              category: "expense" },
  { code: "6200", name: "Software & Technology",        category: "expense" },
  { code: "6300", name: "Insurance – Liability",        category: "expense" },
  { code: "6400", name: "Payroll Processing Fees",      category: "expense" },
  { code: "6500", name: "Legal & Professional",         category: "expense" },
  { code: "6600", name: "Marketing & Advertising",      category: "expense" },
  { code: "2100", name: "Due To – Mgmt Co (IC)",        category: "ic_due_to" },
  { code: "2150", name: "Due To – Affiliate (IC)",      category: "ic_due_to" },
  { code: "1100", name: "Due From – Property (IC)",     category: "ic_due_from" },
  { code: "1150", name: "Due From – Affiliate (IC)",    category: "ic_due_from" },
  { code: "2000", name: "Accounts Payable",             category: "accounts_payable" },
];

// vendor, description, cost, start offset (months from now), term, prepaidGL, expenseGL, entity
const SAMPLE_SCHEDULES = [
  ["Travelers Insurance", "Property Insurance — Elmwood",        24000, -6, 12, "1500-001", "6200-001", "Elmwood Apartments"],
  ["Travelers Insurance", "Property Insurance — Carrington",     18000, -6, 12, "1500-001", "6200-001", "The Carrington"],
  ["Travelers Insurance", "Property Insurance — Harbor View",    28800, -5, 12, "1500-001", "6200-001", "Harbor View Tower"],
  ["Entrata Inc.",        "Software Platform Annual License",    36000, -3, 12, "1500-002", "6200-002", "Corporate"],
  ["Yardi Systems",       "Yardi Voyager Add-On Module",         14400, -1, 12, "1500-002", "6200-002", "Corporate"],
  ["Chubb Insurance",     "D&O / E&O Policy",                    48000,  0, 12, "1500-003", "6200-003", "Corporate"],
  ["HVAC Solutions LLC",  "Annual HVAC Service Agreement",        9600, -2, 12, "1500-004", "6300-001", "Pine Ridge Estates"],
  ["ProLandscape Inc.",   "Grounds Maintenance Contract",        18000, -4, 12, "1500-004", "6300-001", "Sunstone Villas"],
  ["City of Riverside",   "Annual Business License",              3600, -8, 12, "1500-005", "6400-001", "Riverside Lofts"],
];

const SAMPLE_IC_TXNS = [
  { off: 0,  vendor: "Yardi Systems",      gl: "6200", amount: 4800,  memo: "Monthly SaaS license",       status: "pending" },
  { off: 0,  vendor: "Gusto Payroll",      gl: "6400", amount: 1250,  memo: "Payroll processing",         status: "pending" },
  { off: -1, vendor: "CNA Insurance",      gl: "6300", amount: 18600, memo: "Quarterly liability premium", status: "allocated" },
  { off: 0,  vendor: "Baker McKenzie LLP", gl: "6500", amount: 3400,  memo: "Legal review – leases",      status: "pending" },
  { off: 0,  vendor: "CoStar Group",       gl: "6600", amount: 2100,  memo: "Market data subscription",   status: "pending" },
];

// vendor, invoiceNo, monthsAgo, amount, description, expenseGL, methodDriver, postMode, lines [entityName, amount, pct]
const SAMPLE_IC_JOURNALS = [
  ["Yardi Systems", "YRD-01", 2, 4800, "Yardi SaaS License", "6200", "units", "both", [
    ["Elmwood Apartments", 579.86, 12.08], ["Riverside Lofts", 231.94, 4.83],
    ["The Carrington", 1015.11, 21.15], ["Millbrook Commons", 347.84, 7.25],
    ["Sunstone Villas", 459.21, 9.57], ["Harbor View Tower", 1450.15, 30.21],
    ["Pine Ridge Estates", 289.83, 6.04], ["Westgate Flats", 426.06, 8.87]]],
  ["Gusto Payroll", "GST-01", 2, 1250, "Payroll Processing", "6400", "employees", "both", [
    ["Elmwood Apartments", 147.06, 11.76], ["Riverside Lofts", 73.53, 5.88],
    ["The Carrington", 257.35, 20.59], ["Millbrook Commons", 110.29, 8.82],
    ["Sunstone Villas", 110.29, 8.82], ["Harbor View Tower", 367.65, 29.41],
    ["Pine Ridge Estates", 73.53, 5.88], ["Westgate Flats", 110.30, 8.84]]],
  ["CNA Insurance", "CNA-Q1", 1, 18600, "Quarterly Liability Premium", "6300", "units", "both", [
    ["Elmwood Apartments", 2247.43, 12.08], ["Riverside Lofts", 898.97, 4.83],
    ["The Carrington", 3933.03, 21.15], ["Millbrook Commons", 1348.44, 7.25],
    ["Sunstone Villas", 1779.96, 9.57], ["Harbor View Tower", 5621.35, 30.22],
    ["Pine Ridge Estates", 1124.27, 6.04], ["Westgate Flats", 1646.55, 8.85]]],
  ["Baker McKenzie LLP", "BM-02", 1, 3400, "Legal Review — Leases", "6500", "equal", "entity_only", [
    ["Elmwood Apartments", 425, 12.5], ["Riverside Lofts", 425, 12.5],
    ["The Carrington", 425, 12.5], ["Millbrook Commons", 425, 12.5],
    ["Sunstone Villas", 425, 12.5], ["Harbor View Tower", 425, 12.5],
    ["Pine Ridge Estates", 425, 12.5], ["Westgate Flats", 425, 12.5]]],
];

async function insertOrThrow(table, rows, select) {
  const q = supabase.from(table).insert(rows);
  const { data, error } = select ? await q.select(select) : await q;
  if (error) throw new Error(`${table}: ${error.message}`);
  return data;
}

export async function seedSampleData(orgId, userId) {
  const today = ym(0);

  const entities = await insertOrThrow("entities",
    SAMPLE_ENTITIES.map((e) => ({ ...e, org_id: orgId })), "id,name");
  const entByName = Object.fromEntries(entities.map((e) => [e.name, e.id]));

  const gls = await insertOrThrow("gl_accounts",
    SAMPLE_GLS.map((g) => ({ ...g, org_id: orgId })), "id,code");
  const glByCode = Object.fromEntries(gls.map((g) => [g.code, g.id]));

  await insertOrThrow("erp_connections",
    [{ org_id: orgId, erp: "entrata", label: "Entrata", status: "connected" }]);

  // AmortIQ schedules, lines, and postings for already-elapsed months
  const postings = [];
  const audit = [];
  for (const [vendor, description, cost, off, months, pGL, eGL, entity] of SAMPLE_SCHEDULES) {
    const startYm = ym(off);
    const [sched] = await insertOrThrow("amort_schedules", [{
      org_id: orgId, entity_id: entByName[entity] || null, vendor, description,
      invoice_ref: `INV-${startYm.replace("-", "")}-${Math.floor(Math.random() * 900 + 100)}`,
      total_cost: cost, start_period: firstOfMonth(startYm), months,
      prepaid_gl_id: glByCode[pGL], expense_gl_id: glByCode[eGL], entered_by: userId,
    }], "id");
    const base = Math.floor((cost / months) * 100) / 100;
    const last = +(cost - base * (months - 1)).toFixed(2);
    const lineRows = Array.from({ length: months }, (_, i) => ({
      org_id: orgId, schedule_id: sched.id, seq: i + 1,
      period: firstOfMonth(ym(off + i)), amount: i === months - 1 ? last : base,
    }));
    const lines = await insertOrThrow("amort_schedule_lines", lineRows, "id,period,amount");
    for (const l of lines) {
      if (l.period.slice(0, 7) < today) {
        const jeRef = `JE-${Math.floor(Math.random() * 90000 + 10000)}`;
        postings.push({
          org_id: orgId, line_id: l.id, je_ref: jeRef, batch_id: "BATCH-SEED",
          memo: `[AmortIQ] ${jeRef} — Amortize — ${description} (${vendor})`,
          source: "sample", posted_by: userId,
        });
        audit.push({ org_id: orgId, module: "amortiq", action: "POST", ref: l.id, actor_id: userId, note: `${jeRef} · sample · BATCH-SEED` });
      }
    }
  }
  if (postings.length) await insertOrThrow("amort_postings", postings);

  // IntercoIQ queue + journals
  await insertOrThrow("ic_transactions", SAMPLE_IC_TXNS.map((t) => ({
    org_id: orgId, txn_date: firstOfMonth(ym(t.off)), vendor: t.vendor,
    gl_id: glByCode[t.gl], amount: t.amount, memo: t.memo, source: "import", status: t.status,
  })));

  const { data: methods, error: mErr } = await supabase.from("alloc_methods").select("id,driver").eq("org_id", orgId);
  if (mErr) throw new Error(`alloc_methods: ${mErr.message}`);
  const methodByDriver = Object.fromEntries(methods.map((m) => [m.driver, m.id]));

  for (const [vendor, invoiceNo, ago, amount, description, eGL, driver, postMode, lines] of SAMPLE_IC_JOURNALS) {
    const jeDate = firstOfMonth(ym(-ago));
    const [je] = await insertOrThrow("ic_journals", [{
      org_id: orgId, vendor, invoice_no: invoiceNo, je_date: jeDate, amount, description,
      memo_tag: "[ICPIQ]", expense_gl_id: glByCode[eGL],
      ic_credit_gl_id: glByCode["2100"], ic_debit_gl_id: glByCode["1100"],
      method_id: methodByDriver[driver], post_mode: postMode,
      allocation_key: `${vendor.toLowerCase()}::${invoiceNo.toLowerCase()}::${jeDate}::${methodByDriver[driver]}`,
      status: "posted", posted_by: userId, posted_at: new Date(`${jeDate}T09:00:00Z`).toISOString(),
    }], "id");
    await insertOrThrow("ic_journal_lines", lines.map(([ent, amt, pct]) => ({
      org_id: orgId, journal_id: je.id, entity_id: entByName[ent], amount: amt, pct,
    })));
    audit.push({ org_id: orgId, module: "intercoiq", action: "POST", ref: je.id, actor_id: userId, note: `${vendor} · ${invoiceNo} · sample` });
  }

  await insertOrThrow("entity_groups", [
    { org_id: orgId, name: "Southeast Portfolio", filters: { region: "Southeast" } },
    { org_id: orgId, name: "Affordable Division", filters: { division: "Affordable" } },
    { org_id: orgId, name: "Managed Entities", filters: { ownership: "Managed" } },
  ]);

  if (audit.length) await insertOrThrow("audit_log", audit);

  const settings = { sample_data: true, watched_gls: ["6200", "6400", "6500"], close_period: today };
  const { error: sErr } = await supabase.from("organizations").update({ settings }).eq("id", orgId);
  if (sErr) throw new Error(`settings: ${sErr.message}`);
}

// ─── SCREENS ─────────────────────────────────────────────────────────────────

function EyeIcon({ off }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

function PasswordField({ value, onChange, placeholder, show, onToggle, minLength = 6 }) {
  return (
    <div className="pq-pwwrap">
      <input className="pq-inp" type={show ? "text" : "password"} required minLength={minLength} value={value} onChange={onChange} placeholder={placeholder} />
      <button type="button" className="pq-eye" onClick={onToggle} title={show ? "Hide password" : "Show password"} aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}>
        <EyeIcon off={show} />
      </button>
    </div>
  );
}

function AuthScreen({ appName }) {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function switchMode(next) {
    setMode(next); setErr(null); setConfirm(""); setShowPw(false); setShowPw2(false);
  }

  const pwRules = [
    { ok: password.length >= 10, label: "At least 10 characters" },
    { ok: /[a-zA-Z]/.test(password), label: "Contains a letter" },
    { ok: /[0-9]/.test(password), label: "Contains a number" },
  ];
  const pwOk = pwRules.every((r) => r.ok);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (mode === "signup" && !pwOk) {
      setErr("Your password doesn't meet the requirements below yet.");
      return;
    }
    if (mode === "signup" && password !== confirm) {
      setErr("Passwords do not match — please re-enter them.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (ex) {
      setErr(ex.message || String(ex));
      setBusy(false);
    }
  }

  return (
    <div className="pq-auth-wrap">
      <style>{AUTH_CSS}</style>
      <form className="pq-card" onSubmit={submit}>
        <div className="pq-logo">{appName === "IntercoIQ" ? <>Interco<em>IQ</em></> : <>Amort<em>IQ</em></>}</div>
        <div className="pq-sub">PIQ Platform</div>
        <label className="pq-lbl">Email</label>
        <input className="pq-inp" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoFocus />
        <label className="pq-lbl">Password</label>
        <PasswordField value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "signup" ? "10+ characters, letters and numbers" : "••••••••"} show={showPw} onToggle={() => setShowPw((v) => !v)} minLength={mode === "signup" ? 10 : 6} />
        {mode === "signup" && (
          <>
            <div className="pq-rules">
              {pwRules.map((r) => (
                <div key={r.label} className={`pq-rule ${r.ok ? "ok" : ""}`}>
                  <span className="pq-dot">✓</span>{r.label}
                </div>
              ))}
            </div>
            <label className="pq-lbl">Confirm password</label>
            <PasswordField value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type it again" show={showPw2} onToggle={() => setShowPw2((v) => !v)} minLength={10} />
            {confirm.length > 0 && confirm !== password && <div className="pq-mismatch">Passwords don't match yet</div>}
          </>
        )}
        {err && <div className="pq-err">{err}</div>}
        <button className="pq-btn" type="submit" disabled={busy}>
          {busy ? "One moment…" : mode === "signup" ? "Create account" : "Sign in"}
        </button>
        <div className="pq-toggle">
          {mode === "signup" ? <>Already have an account? <a onClick={() => switchMode("signin")}>Sign in</a></>
            : <>New to PIQ? <a onClick={() => switchMode("signup")}>Create an account</a></>}
        </div>
        <div className="pq-note">One PIQ account works on both AmortIQ and IntercoIQ.</div>
      </form>
    </div>
  );
}

function Onboarding({ session, onDone }) {
  const [orgName, setOrgName] = useState("");
  const [busy, setBusy] = useState(null); // null | 'sample' | 'empty'
  const [err, setErr] = useState(null);

  async function create(withSample) {
    if (!orgName.trim()) { setErr("Give your company a name first."); return; }
    setBusy(withSample ? "sample" : "empty"); setErr(null);
    try {
      const { data: orgId, error } = await supabase.rpc("create_organization", { org_name: orgName.trim() });
      if (error) throw error;
      if (withSample) {
        await seedSampleData(orgId, session.user.id);
      } else {
        const settings = { sample_data: false, watched_gls: [], close_period: ym(0) };
        await supabase.from("organizations").update({ settings }).eq("id", orgId);
      }
      await onDone();
    } catch (ex) {
      setErr(ex.message || String(ex));
      setBusy(null);
    }
  }

  return (
    <div className="pq-auth-wrap">
      <style>{AUTH_CSS}</style>
      <div className="pq-card">
        <div className="pq-logo">PIQ <em>Platform</em></div>
        <div className="pq-sub">Set up your workspace</div>
        <label className="pq-lbl">Company / organization name</label>
        <input className="pq-inp" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Lynch Property Group" autoFocus />
        {err && <div className="pq-err">{err}</div>}
        {busy ? (
          <div className="pq-busy">Setting up your workspace{busy === "sample" ? " and loading sample data" : ""}… this takes a few seconds.</div>
        ) : (
          <>
            <button className="pq-btn" onClick={() => create(true)}>Start with sample data</button>
            <button className="pq-btn pq-btn-ghost" onClick={() => create(false)}>Start empty</button>
          </>
        )}
        <div className="pq-note">Sample data lets you explore both modules with realistic entities, schedules, and allocations. You can start fresh anytime.</div>
      </div>
    </div>
  );
}

export default function AuthGate({ appName, children }) {
  const [session, setSession] = useState(undefined); // undefined = booting
  const [ctx, setCtx] = useState(null);              // { profile, org, role }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => setSession(s ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  async function loadCtx() {
    const { data: s } = await supabase.auth.getSession();
    const user = s.session?.user;
    if (!user) { setCtx(null); return; }
    const [{ data: profile }, { data: mem }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
      supabase.from("memberships").select("role, organizations(*)").eq("user_id", user.id).limit(1).maybeSingle(),
    ]);
    setCtx({
      profile: profile || { id: user.id, email: user.email, display_name: user.email.split("@")[0] },
      org: mem?.organizations || null,
      role: mem?.role || null,
    });
  }

  useEffect(() => {
    if (session) loadCtx();
    else if (session === null) setCtx(null);
  }, [session]);

  if (session === undefined || (session && !ctx)) {
    return (
      <div className="pq-auth-wrap"><style>{AUTH_CSS}</style>
        <div className="pq-busy">Loading…</div>
      </div>
    );
  }
  if (!session) return <AuthScreen appName={appName} />;
  if (!ctx.org) return <Onboarding session={session} onDone={loadCtx} />;

  return children({
    session,
    profile: ctx.profile,
    org: ctx.org,
    role: ctx.role,
    refreshOrg: loadCtx,
    signOut: () => supabase.auth.signOut(),
  });
}
